import React from "react";
import { motion } from "framer-motion";
import { 
  X, FileText, Bug, Zap, Layers, Clock, CheckCircle, 
  User, Code, TestTube2, File, AlertTriangle, Calendar
} from "lucide-react";

function TicketDetail({ ticket, onClose }) {
  // Fonction pour obtenir la couleur en fonction de la priorité
  const getPriorityColor = (priority) => {
    switch(priority) {
      case "1": return "bg-red-100 text-red-800";
      case "2": return "bg-amber-100 text-amber-800";
      case "3": return "bg-blue-100 text-blue-800";
      default: return "bg-gray-100 text-gray-800";
    }
  };

  // Fonction pour obtenir l'icône de priorité
  const getPriorityIcon = (priority) => {
    switch(priority) {
      case "1": return <Zap className="w-4 h-4" />;
      case "2": return <AlertTriangle className="w-4 h-4" />;
      case "3": return <Clock className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 20 }}
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1000] p-4"
    >
      <motion.div 
        className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col"
        layout
      >
        {/* Header */}
        <div className="border-b border-gray-200 p-6 flex justify-between items-start">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h2 className="text-2xl font-bold text-gray-800">{ticket.nom}</h2>
              <span className={`text-xs px-2 py-1 rounded-full ${getPriorityColor(ticket.priorite)} flex items-center gap-1`}>
                {getPriorityIcon(ticket.priorite)}
                {ticket.priorite === "1" ? "Urgent" : 
                 ticket.priorite === "2" ? "Élevée" : 
                 ticket.priorite === "3" ? "Moyenne" : "Basse"}
              </span>
            </div>
            <div className="flex items-center text-sm text-gray-500 gap-4">
              <span className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                Créé le {new Date(ticket.created_at).toLocaleDateString()}
              </span>
              <span className="flex items-center gap-1">
                {ticket.type === "bug" ? <Bug className="w-4 h-4" /> : <FileText className="w-4 h-4" />}
                {ticket.type === "bug" ? "Bug" : "Demande"}
              </span>
            </div>
          </div>
          <button 
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto p-6 space-y-6">
          {/* Description Section */}
          <div>
            <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-800 mb-3">
              <FileText className="w-5 h-5 text-blue-500" />
              Description
            </h3>
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <p className="text-gray-700 whitespace-pre-line">{ticket.description || "Aucune description fournie"}</p>
            </div>
          </div>

          {/* Grid Sections */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Informations de base */}
            <div className="bg-gray-50 rounded-lg border border-gray-200 p-5">
              <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-800 mb-4">
                <Layers className="w-5 h-5 text-blue-500" />
                Informations
              </h3>
              <div className="space-y-3">
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Module</p>
                  <p className="text-gray-700">{ticket.module?.nom || "Non spécifié"}</p>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Statut</p>
                  <p className="text-gray-700">
                    {ticket.status === "1" ? "En cours" : 
                     ticket.status === "2" ? "Terminé" : 
                     ticket.status === "3" ? "Livré" : "Nouveau"}
                  </p>
                </div>
                {ticket.validation_adminCl && (
                  <div>
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Validation</p>
                    <p className="text-gray-700">{ticket.validation_adminCl?.nom || "Non spécifié"}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Dates importantes */}
            <div className="bg-gray-50 rounded-lg border border-gray-200 p-5">
              <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-800 mb-4">
                <Calendar className="w-5 h-5 text-blue-500" />
                Chronologie
              </h3>
              <div className="space-y-3">
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Début technique</p>
                  <p className="text-gray-700">
                    {ticket.date_debut_technique ? new Date(ticket.date_debut_technique).toLocaleDateString() : "Non spécifié"}
                  </p>
                </div>
                {ticket.date_fin_tech && (
                  <div>
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Fin technique</p>
                    <p className="text-gray-700">{new Date(ticket.date_fin_tech).toLocaleDateString()}</p>
                  </div>
                )}
                {ticket.date_livraison && (
                  <div>
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Livraison</p>
                    <p className="text-gray-700">{new Date(ticket.date_livraison).toLocaleDateString()}</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Bug Details (if applicable) */}
          {ticket.type === "bug" && ticket.bug && (
            <div className="bg-red-50 rounded-lg border border-red-200 p-5">
              <h3 className="flex items-center gap-2 text-lg font-semibold text-red-800 mb-4">
                <Bug className="w-5 h-5 text-red-500" />
                Détails du Bug
              </h3>
              <div>
                <p className="text-xs font-medium text-red-500 uppercase tracking-wider">Cause</p>
                <p className="text-red-700">{ticket.bug.cause_bug || "Non spécifiée"}</p>
              </div>
            </div>
          )}

          {/* Documents */}
          {ticket.documents && ticket.documents.length > 0 && (
            <div>
              <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-800 mb-3">
                <File className="w-5 h-5 text-blue-500" />
                Documents
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {ticket.documents.map((doc, index) => (
                  <a
                    key={index}
                    href={doc.document_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 p-3 bg-white border border-gray-200 rounded-lg hover:bg-blue-50 transition-colors"
                  >
                    <div className="bg-blue-100 p-2 rounded-lg">
                      <File className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-800">Document {index + 1}</p>
                      <p className="text-xs text-gray-500 truncate max-w-[180px]">
                        {doc.document_url.split('/').pop()}
                      </p>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Équipe */}
          <div>
            <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-800 mb-3">
              <User className="w-5 h-5 text-blue-500" />
              Équipe
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <div className="flex items-center gap-3 mb-3">
                  <div className="bg-blue-100 p-2 rounded-full">
                    <User className="w-5 h-5 text-blue-600" />
                  </div>
                  <h4 className="font-medium text-gray-800">Créateur</h4>
                </div>
                <p className="text-gray-700">
                  {ticket.createur?.prenom} {ticket.createur?.nom}
                </p>
              </div>

              {ticket.testeur && (
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="bg-purple-100 p-2 rounded-full">
                      <TestTube2 className="w-5 h-5 text-purple-600" />
                    </div>
                    <h4 className="font-medium text-gray-800">Testeur</h4>
                  </div>
                  <p className="text-gray-700">
                    {ticket.testeur?.prenom} {ticket.testeur?.nom}
                  </p>
                </div>
              )}

              {ticket.developpeurs && ticket.developpeurs.length > 0 && (
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="bg-green-100 p-2 rounded-full">
                      <Code className="w-5 h-5 text-green-600" />
                    </div>
                    <h4 className="font-medium text-gray-800">Développeur{ticket.developpeurs.length > 1 ? "s" : ""}</h4>
                  </div>
                  <div className="space-y-1">
                    {ticket.developpeurs.map((dev, index) => (
                      <p key={index} className="text-gray-700">
                        {dev.prenom} {dev.nom}
                      </p>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 p-4 bg-gray-50 rounded-b-xl flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg transition-colors font-medium"
          >
            Fermer
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

export default TicketDetail;